import { MetricCard } from "../components/MetricCard";
import { StatusPill } from "../components/StatusPill";
import { SectionHeader } from "../components/SectionHeader";
import { formatUptime } from "../lib/format";
import { liveTone } from "../lib/status";
import { fetchDashboardOverview } from "../lib/dashboard";
import { DashboardStateView, useDashboardQuery } from "../hooks/useDashboardQuery";

export function OverviewPage() {
  const state = useDashboardQuery(fetchDashboardOverview);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Operations overview</h1>
          <p>
            Authenticated tenant-scoped control-plane posture. No market PnL, balances, or preview
            business entities.
          </p>
        </div>
        <StatusPill label="Read-only" tone="neutral" />
      </div>

      <DashboardStateView
        state={state}
        emptyTitle="No operational entities yet"
        emptyDetail="Counts are zero for this tenant until leaders, followers, or brokers are registered."
      >
        {(data) => (
          <>
            <div className="metrics-grid">
              <MetricCard
                label="Leaders"
                value={String(data.counts.leaders)}
                hint="Registered signal providers"
              />
              <MetricCard
                label="Followers"
                value={String(data.counts.followers)}
                hint="Registered follower accounts"
              />
              <MetricCard
                label="Relationships"
                value={String(data.counts.activeRelationships)}
                hint={`${data.counts.relationships} total`}
              />
              <MetricCard
                label="Live execution"
                value={data.liveExecutionEnabled ? "ENABLED" : "Disabled"}
                hint={`Mode ${data.executionMode}`}
                trailing={
                  <StatusPill
                    label={data.liveExecutionEnabled ? "Breach" : "Safe"}
                    tone={liveTone(data.liveExecutionEnabled)}
                  />
                }
              />
            </div>

            <div className="content-grid">
              <div className="panel">
                <SectionHeader title="Platform posture" subtitle={`Tenant ${data.tenantId}`} />
                <div className="panel-body kv-grid">
                  <div className="kv-item">
                    <div className="k">Bridge mode</div>
                    <div className="v">{data.bridgeMode}</div>
                  </div>
                  <div className="kv-item">
                    <div className="k">Rollout</div>
                    <div className="v">{data.safety.rolloutMode}</div>
                  </div>
                  <div className="kv-item">
                    <div className="k">Kill switch</div>
                    <div className="v">{data.safety.killSwitchActive ? "ACTIVE" : "Inactive"}</div>
                  </div>
                  <div className="kv-item">
                    <div className="k">Open risk violations</div>
                    <div className="v">{data.counts.openRiskViolations}</div>
                  </div>
                  <div className="kv-item">
                    <div className="k">Active risk policies</div>
                    <div className="v">{data.counts.activeRiskPolicies}</div>
                  </div>
                  <div className="kv-item">
                    <div className="k">Brokers</div>
                    <div className="v">{data.counts.brokers}</div>
                  </div>
                  <div className="kv-item">
                    <div className="k">Active API keys</div>
                    <div className="v">{data.counts.activeApiKeys}</div>
                  </div>
                  <div className="kv-item">
                    <div className="k">Storage / uptime</div>
                    <div className="v">
                      {data.storageMode} · {formatUptime(data.uptimeSeconds)}
                    </div>
                  </div>
                  {data.monitoring && (
                    <>
                      <div className="kv-item">
                        <div className="k">SLO state</div>
                        <div className="v">
                          {data.monitoring.sloStatus} · {data.monitoring.sampleCount} samples
                        </div>
                      </div>
                      <div className="kv-item">
                        <div className="k">Firing alerts</div>
                        <div className="v">{data.monitoring.firingAlertCount}</div>
                      </div>
                      <div className="kv-item">
                        <div className="k">TLS / external monitor</div>
                        <div className="v">{data.monitoring.tlsNote}</div>
                      </div>
                      <div className="kv-item">
                        <div className="k">Rollback readiness</div>
                        <div className="v">
                          {data.monitoring.rollbackReady ? "Plan tooling ready" : "Not ready"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </DashboardStateView>
    </>
  );
}
